import React, { useState } from 'react';
import { useEffect } from 'react';
import { X, Bell, Mail, MessageCircle, Send, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import type { EventRow } from '../types/event';
import { pushGA } from '../lib/utils';
import useAuth from '../hooks/useAuth';

interface ReminderModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: EventRow;
}

type ReminderType = 'email' | 'whatsapp' | 'ambos';

interface FormData {
  name: string;
  email: string;
  whatsapp: string;
}

export default function ReminderModal({ isOpen, onClose, event }: ReminderModalProps) {
  const { user } = useAuth();
  const [reminderType, setReminderType] = useState<ReminderType>('email');
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    whatsapp: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Pré-preencher com dados do perfil quando o modal abrir
  useEffect(() => {
    if (isOpen && user) {
      const profileData = {
        name: user.display_name || '',
        email: user.email || '',
        whatsapp: user.phone || ''
      };
      
      setFormData(profileData);
      
      // Determinar tipo de lembrete baseado nos dados disponíveis
      const hasEmail = !!profileData.email;
      const hasPhone = !!profileData.whatsapp;
      
      if (hasEmail && hasPhone) {
        setReminderType('ambos');
      } else if (hasPhone) {
        setReminderType('whatsapp');
      } else {
        setReminderType('email');
      }
    }
  }, [isOpen, user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        reminderType,
        name: formData.name,
        email: reminderType === 'email' || reminderType === 'ambos' ? formData.email : undefined,
        whatsapp: reminderType === 'whatsapp' || reminderType === 'ambos' ? formData.whatsapp : undefined,
        event: {
          id: event.id,
          title: event.title,
          description: event.description,
          start_iso: event.start_iso,
          end_iso: event.end_iso,
          join_url: event.join_url,
          speakers: event.speakers,
          tags: event.tags
        }
      };

      const response = await fetch('https://webhook.ygorgoncalves.com/webhook/me-lembra-cro', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setShowSuccess(true);
        pushGA('reminder_set', { 
          event_id: event.id, 
          reminder_type: reminderType,
          title: event.title 
        });
        
        setTimeout(() => {
          setShowSuccess(false);
          onClose();
        }, 2000);
      } else {
        throw new Error('Falha ao enviar lembrete');
      }
    } catch (error) {
      console.error('Erro ao enviar lembrete:', error);
      alert('Erro ao configurar lembrete. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      setShowSuccess(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-soft-black/60 dark:bg-soft-black/80 backdrop-blur-sm"
            onClick={handleClose}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-md bg-white dark:bg-soft-black rounded-2xl border border-light-gray-alt dark:border-deep-purple/50 shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-light-gray-alt dark:border-deep-purple/30">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-darling-lilac/10 dark:bg-darling-lilac/20">
                  <Bell className="h-5 w-5 text-darling-lilac" />
                </div>
                <div>
                  <h3 className="text-lg font-exo font-bold text-soft-black dark:text-white">
                    Me lembra
                  </h3>
                  <p className="text-sm text-soft-black/60 dark:text-light-gray/70 font-exo">
                    {event.title}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                disabled={isSubmitting}
                className="p-2 rounded-lg hover:bg-light-gray dark:hover:bg-deep-purple/30 transition-colors disabled:opacity-50"
              >
                <X className="h-5 w-5 text-soft-black/60 dark:text-light-gray/60" />
              </button>
            </div>

            {/* Success Message */}
            <AnimatePresence>
              {showSuccess && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="absolute inset-0 flex items-center justify-center bg-white dark:bg-soft-black z-10"
                >
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-ocean-water/10 dark:bg-ocean-water/20 flex items-center justify-center">
                      <Bell className="h-8 w-8 text-ocean-water" />
                    </div>
                    <h4 className="text-xl font-exo font-bold text-soft-black dark:text-white mb-2">
                      Vou te lembrar na data!
                    </h4>
                    <p className="text-sm text-soft-black/60 dark:text-light-gray/70 font-exo">
                      Você receberá um lembrete antes do evento
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Reminder Type Selection */}
              <div>
                <label className="block text-sm font-exo font-medium text-soft-black dark:text-white mb-3">
                  Por onde quer ser lembrado?
                </label>
                <div className="grid grid-cols-1 gap-3">
                  <button
                    type="button"
                    onClick={() => setReminderType('email')}
                    className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                      reminderType === 'email'
                        ? 'border-darling-lilac bg-darling-lilac/10 dark:bg-darling-lilac/20'
                        : 'border-light-gray-alt dark:border-deep-purple/50 hover:border-darling-lilac/50'
                    }`}
                  >
                    <Mail className="h-5 w-5 text-darling-lilac" />
                    <span className="font-exo font-medium text-soft-black dark:text-white">
                      Email
                    </span>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setReminderType('whatsapp')}
                    className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                      reminderType === 'whatsapp'
                        ? 'border-darling-lilac bg-darling-lilac/10 dark:bg-darling-lilac/20'
                        : 'border-light-gray-alt dark:border-deep-purple/50 hover:border-darling-lilac/50'
                    }`}
                  >
                    <MessageCircle className="h-5 w-5 text-darling-lilac" />
                    <span className="font-exo font-medium text-soft-black dark:text-white">
                      WhatsApp
                    </span>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setReminderType('ambos')}
                    className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                      reminderType === 'ambos'
                        ? 'border-darling-lilac bg-darling-lilac/10 dark:bg-darling-lilac/20'
                        : 'border-light-gray-alt dark:border-deep-purple/50 hover:border-darling-lilac/50'
                    }`}
                  >
                    <div className="flex items-center gap-1">
                      <Mail className="h-4 w-4 text-darling-lilac" />
                      <MessageCircle className="h-4 w-4 text-darling-lilac" />
                    </div>
                    <span className="font-exo font-medium text-soft-black dark:text-white">
                      Ambos
                    </span>
                  </button>
                </div>
              </div>

              {/* Form Fields */}
              <div className="space-y-4">
                {/* Nome */}
                <div>
                  <label className="block text-sm font-exo font-medium text-soft-black dark:text-white mb-2">
                    Nome
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-light-gray-alt dark:border-deep-purple/50 bg-white dark:bg-soft-black text-soft-black dark:text-white font-exo focus:ring-2 focus:ring-darling-lilac/20 focus:border-darling-lilac/50 transition-all"
                    placeholder="Seu nome"
                  />
                </div>

                {/* Email */}
                {(reminderType === 'email' || reminderType === 'ambos') && (
                  <div>
                    <label className="block text-sm font-exo font-medium text-soft-black dark:text-white mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl border border-light-gray-alt dark:border-deep-purple/50 bg-white dark:bg-soft-black text-soft-black dark:text-white font-exo focus:ring-2 focus:ring-darling-lilac/20 focus:border-darling-lilac/50 transition-all"
                      placeholder="seu@email.com"
                    />
                  </div>
                )}

                {/* WhatsApp */}
                {(reminderType === 'whatsapp' || reminderType === 'ambos') && (
                  <div>
                    <label className="block text-sm font-exo font-medium text-soft-black dark:text-white mb-2">
                      WhatsApp
                    </label>
                    <input
                      type="tel"
                      required
                      value={formData.whatsapp}
                      onChange={(e) => setFormData(prev => ({ ...prev, whatsapp: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl border border-light-gray-alt dark:border-deep-purple/50 bg-white dark:bg-soft-black text-soft-black dark:text-white font-exo focus:ring-2 focus:ring-darling-lilac/20 focus:border-darling-lilac/50 transition-all"
                      placeholder="+55 11 99999-9999"
                    />
                    <p className="mt-1 text-xs text-soft-black/60 dark:text-light-gray/70 font-exo">
                      Inclua o código do país (ex: +55 para Brasil)
                    </p>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-darling-lilac hover:bg-deep-purple disabled:bg-darling-lilac/50 text-white font-exo font-semibold transition-all duration-200 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Configurando...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Configurar lembrete
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}